import React from "react";
import { motion } from "framer-motion";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";
const { FiArrowRight, FiPlay, FiZap, FiLeaf } = FiIcons;

export default function Hero() {
  const scrollToQuote = () => {
    document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToFeatures = () => {
    document.querySelector("#features")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      // 64px header height → offset with pt-20, and ensure hero fills viewport under header
      className="relative min-h-[calc(100vh-64px)] flex items-center justify-center overflow-hidden pt-20"
      aria-label="Hero section – Green Gas Turbines for a Net-Zero Grid"
    >
      {/* Background */}
      <div className="absolute inset-0 isolate">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900" />
        <div
          className="absolute inset-0 opacity-30 mix-blend-overlay"
          style={{
            backgroundImage: "url('/green-gas-turbine-hero.webp')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,0,0,0.65)_0%,_rgba(0,0,0,0)_70%)]" />
      </div>

      {/* Ambient dots (kept) */}
      <div className="pointer-events-none absolute inset-0">
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={`left-${i}`}
            className="absolute h-1.5 w-1.5 rounded-full bg-primary-400/30"
            animate={{ y: [0, -18, 0], opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 3 + i, repeat: Infinity, delay: i * 0.4 }}
            style={{ left: `${15 + i * 18}%`, top: `${35 + i * 12}%` }}
          />
        ))}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`right-${i}`}
            className="absolute h-1.5 w-1.5 rounded-full bg-primary-400/30"
            animate={{ y: [0, -15, 0], opacity: [0.3, 0.7, 0.3] }}
            transition={{ duration: 2.5 + i, repeat: Infinity, delay: i * 0.3 }}
            style={{ right: `${20 + i * 15}%`, top: `${40 + i * 15}%` }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-4 text-center pb-28 sm:pb-28 md:pb-36">
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="px-2 text-4xl font-bold leading-tight text-white sm:text-5xl md:text-6xl xl:text-7xl"
        >
          <span className="block text-green-400">Green&nbsp;Gas&nbsp;Turbines</span>
          <span className="block text-primary-300">for a Net-Zero&nbsp;Grid</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mx-auto mt-4 max-w-3xl px-4 text-base leading-relaxed text-gray-200 sm:text-lg md:text-xl"
        >
          Reduce emissions by up to 85% while maximizing efficiency and ROI with
          next-generation, hydrogen-capable turbine technology.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mx-auto mt-6 flex w-full max-w-2xl flex-col items-center justify-center gap-3 px-4 sm:mt-8 sm:flex-row sm:gap-4"
        >
          <motion.button
            onClick={scrollToQuote}
            className="group w-full rounded-lg bg-primary-700 px-6 py-3 text-base font-semibold text-white shadow-xl hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-500/40 sm:w-auto sm:px-8 sm:py-4 sm:text-lg"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>Get Quote</span>
            <SafeIcon
              icon={FiArrowRight}
              className="ml-2 inline h-4 w-4 transition-transform group-hover:translate-x-1 sm:h-5 sm:w-5"
            />
          </motion.button>

          <motion.button
            onClick={scrollToFeatures}
            className="group w-full rounded-lg border border-white/20 bg-white/10 px-6 py-3 text-base font-semibold text-white backdrop-blur-md hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30 sm:w-auto sm:px-8 sm:py-4 sm:text-lg"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiPlay} className="mr-2 inline h-4 w-4 sm:h-5 sm:w-5" />
            <span>Learn More</span>
          </motion.button>
        </motion.div>

        {/* Micro-trust line */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="px-4 pt-3 text-xs text-gray-300 sm:pt-4 sm:text-sm"
        >
          Trusted by <span className="font-semibold text-white">500+ installations</span> in 45&nbsp;countries.
        </motion.p>

        {/* Key Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mx-auto mt-3 grid max-w-4xl grid-cols-1 gap-3 px-4 sm:mt-4 sm:gap-4 md:grid-cols-3"
        >
          {[
            { icon: FiZap, title: "85% Emission Reduction", desc: "Cleaner than traditional turbines" },
            { icon: FiLeaf, title: "Carbon Neutral Pathways", desc: "Hydrogen & bio-RNG ready" },
            { icon: FiArrowRight, title: "40% Efficiency Gain", desc: "Advanced combustion design" },
          ].map((b, idx) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + idx * 0.12 }}
              className="rounded-lg border border-white/20 bg-white/10 p-4 backdrop-blur-md sm:p-6"
            >
              <SafeIcon icon={b.icon} className="mx-auto mb-2 h-6 w-6 text-primary-300 sm:mb-3 sm:h-8 sm:w-8" />
              <h2 className="mb-1 text-center text-sm font-semibold text-white sm:mb-2 sm:text-lg">{b.title}</h2>
              <p className="text-center text-xs text-gray-300 sm:text-sm">{b.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator – spaced from cards via container pb */}
      <motion.div
        className="absolute bottom-6 left-0 right-0 z-40 flex justify-center md:bottom-8"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <button
          onClick={scrollToFeatures}
          className="group flex flex-col items-center text-white/70 transition-colors duration-300 hover:text-white"
          aria-label="Scroll to features section"
        >
          <div className="flex h-10 w-6 justify-center rounded-full border-2 border-white/40 transition-colors duration-300 group-hover:border-white/60">
            <div className="mt-2 h-3 w-1 rounded-full bg-white/70 transition-colors duration-300 group-hover:bg-white" />
          </div>
          <span className="mt-2 text-xs opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            Explore Features
          </span>
        </button>
      </motion.div>
    </section>
  );
}